import { useEffect, useState } from "react";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { getDealHeroImage } from "../../../services/dealImagesApi";
import { createHeroPreviewCache } from "./heroPreviewCache";

const heroPreviewCache = createHeroPreviewCache(getDealHeroImage);

function HeroPreviewTooltip({ dealId }: { dealId: number }) {
  const [url, setUrl] = useState<string | null | undefined>(() =>
    heroPreviewCache.peek(dealId),
  );

  useEffect(() => {
    if (url !== undefined) return;
    let cancelled = false;
    void heroPreviewCache.get(dealId).then((result) => {
      if (!cancelled) setUrl(result);
    });
    return () => {
      cancelled = true;
    };
  }, [dealId, url]);

  if (url === undefined) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2, minWidth: 120 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!url) {
    return (
      <Typography variant="body2" sx={{ p: 1 }}>
        No primary image
      </Typography>
    );
  }

  return (
    <Box
      component="img"
      src={url}
      alt=""
      sx={{
        display: "block",
        maxWidth: 280,
        maxHeight: 210,
        width: "auto",
        height: "auto",
        objectFit: "contain",
        borderRadius: 1,
      }}
    />
  );
}

interface DealNameLinkProps {
  dealId: number;
  name: string;
  /** Defaults to `/deals/{dealId}`. */
  href?: string;
}

export function DealNameLink({ dealId, name, href }: DealNameLinkProps) {
  const [open, setOpen] = useState(false);

  return (
    <Tooltip
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      title={open ? <HeroPreviewTooltip dealId={dealId} /> : ""}
      placement="right"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            p: 1,
            maxWidth: 300,
            backgroundColor: "#fff",
            color: "text.primary",
            border: "1px solid #e5e7eb",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
          },
        },
        arrow: {
          sx: {
            color: "#fff",
            "&::before": {
              border: "1px solid #e5e7eb",
            },
          },
        },
      }}
    >
      <span style={{ display: "inline-flex", maxWidth: "100%", minWidth: 0 }}>
        <a
          href={href ?? `/deals/${dealId}`}
          style={{
            color: "#1B7A63",
            textDecoration: "underline",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </a>
      </span>
    </Tooltip>
  );
}
